import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import Logo from '../common/Logo';
import '../salon/styles/base.css'; // Import global form styles
import './Auth.css';

const VerifyEmail = () => {
  const { t, translateMessage } = useLanguage();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [showResendForm, setShowResendForm] = useState(false);
  const hasVerifiedRef = useRef(false); // Prevent duplicate verification
  const { verifyEmail } = useAuth();
  const navigate = useNavigate();

  const handleVerification = useCallback(async (verificationToken) => {
    // Prevent duplicate verification attempts (e.g., from React StrictMode)
    if (hasVerifiedRef.current) {
      return;
    }
    
    hasVerifiedRef.current = true;
    setLoading(true);
    setError('');
    setSuccess('');

    const result = await verifyEmail(verificationToken);
    
    if (result.success) {
      setSuccess(translateMessage(result.message));
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } else {
      setError(translateMessage(result.error));
      setShowResendForm(true);
    }
    
    setLoading(false);
  }, [verifyEmail, navigate, translateMessage]);

  const handleResendVerification = async (e) => {
    e.preventDefault();
    setResendLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post('/api/auth/resend-verification', { email });
      console.log('Resend verification response:', response.data);
      setSuccess(translateMessage(response.data.message));
      setShowResendForm(false);
      setEmail('');
    } catch (err) {
      console.error('Resend verification error:', err);
      console.error('Error response:', err.response);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || t('messages.error.resendVerificationFailed');
      setError(translateMessage(errorMessage));
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
      handleVerification(tokenParam);
    } else {
      setError(t('auth.invalidVerificationLink'));
    }
  }, [searchParams, handleVerification, t]);

  if (!token) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <Logo />
            <p>{t('auth.emailVerification')}</p>
          </div>
          <div className="error-message">
            {error}
          </div>
          <div className="auth-links">
            <Link to="/login" className="link">
              {t('auth.goToSignIn')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <Logo />
          <p>{t('auth.verifyingEmail')}</p>
        </div>

        {loading && (
          <div className="loading-message">
            {t('auth.verifyingEmailDesc')}
          </div>
        )}

        {!loading && error && !success && (
          <div className="error-message">
            {error}
          </div>
        )}

        {!loading && success && (
          <div className="success-message">
            {success}
            {!showResendForm && <p>{t('auth.redirectingToLogin')}</p>}
          </div>
        )}

        {showResendForm && !success && (
          <div className="resend-verification-form">
            <p className="resend-info">{t('auth.resendVerificationInfo')}</p>
            <form onSubmit={handleResendVerification}>
              <div className="form-group">
                <label htmlFor="email">{t('auth.emailAddress')}</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.enterYourEmail')}
                  required
                  disabled={resendLoading}
                />
              </div>
              <button
                type="submit"
                className="salon-button primary"
                disabled={resendLoading}
              >
                {resendLoading ? `${t('auth.sending')}...` : t('auth.resendVerificationEmail')}
              </button>
            </form>
          </div>
        )}

        <div className="auth-links">
          <Link to="/login" className="link">
            {t('auth.goToSignIn')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
